import type { OrganizationType } from './domain';

export interface Timestamps {
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string | null;
}

export interface User extends Timestamps {
    id: string;
    email?: string;
    firstName: string;
    lastName: string;
    phone?: string;
    isPlatformAdmin: boolean;
}

export interface Organization extends Timestamps {
    id: string;
    name: string;
    type?: OrganizationType | string;
    country?: string;
    settings?: Record<string, unknown>;
}

/**
 * Effective per-event email suppression for one organization. Every event is
 * always present — the API resolves absent to `true` so the UI never has to
 * guess, and `true` means "send this email".
 */
export interface NotificationSettings {
    reportSubmitted: boolean;
    visitCreated: boolean;
    visitAssigned: boolean;
}

export interface Role extends Timestamps {
    id: string;
    /** Stable lowercase snake_case slug. */
    name: string;
    /** Human-readable label for UI display. */
    displayName: string;
    description?: string;
    scope: 'platform' | 'organization';
}

/** Opt-in feature flag: a single row per key, scoped to a set of organizations with an optional pilot user allowlist. */
export interface FeatureFlag extends Timestamps {
    id: string;
    key: string;
    enabled: boolean;
    /** `null`/empty = all organizations; non-empty = only these organizations. */
    organizationIds?: string[] | null;
    /** When non-empty, only these users see the feature even when enabled. */
    userIds?: string[] | null;
    description?: string | null;
    createdBy?: string | null;
    updatedBy?: string | null;
}

/** Body for `POST /feature-flags`. */
export interface FeatureFlagCreatePayload {
    key: string;
    enabled: boolean;
    organizationIds?: string[];
    userIds?: string[];
    description?: string;
}

/** Body for `PATCH /feature-flags/:id`. Pass an empty `organizationIds` to make the flag global. */
export interface FeatureFlagUpdatePayload {
    key?: string;
    enabled?: boolean;
    organizationIds?: string[];
    userIds?: string[];
    description?: string;
}

/** Org-scoped role (or clonable global template) with its composed permission keys. */
export interface OrgRole extends Role {
    organizationId?: string | null;
    isSystem?: boolean;
    permissionKeys?: string[];
}

/** Response of `GET /organizations/:orgId/roles`. */
export interface OrgRolesList {
    roles: OrgRole[];
    templates: OrgRole[];
}

export type PermissionType = 'view' | 'action';

/** A single entry of `GET /authorization/permissions` (the assignable catalog). */
export interface PermissionCatalogEntry {
    key: string;
    type: PermissionType;
    label: string;
    tab?: string;
    route?: string;
    selfScoped?: boolean;
}

export interface NavManifestEntry {
    key: string;
    route: string;
    requires: string;
    selfScoped: boolean;
}

export interface PermissionCatalogResponse {
    permissions: PermissionCatalogEntry[];
    nav: NavManifestEntry[];
}

/** A role assignment held by a member (multiple per member; union of permissions). */
export interface OrganizationMemberRoleLink extends Timestamps {
    id: string;
    organizationMemberId: string;
    roleId: string;
    role?: Role;
}

export interface OrganizationMember extends Timestamps {
    id: string;
    userId: string;
    organizationId: string;
    roleId: string;
    user?: User;
    organization?: Organization;
    role?: Role;
    memberRoles?: OrganizationMemberRoleLink[];
}

export interface UserRole extends Timestamps {
    id: string;
    userId: string;
    roleId: string;
    user?: User;
    role?: Role;
}

export interface AuditLog extends Timestamps {
    id: string;
    entityType: string;
    entityId: string;
    action: string;
    performedBy?: string;
    diff?: Record<string, unknown> | null;
    context?: {
        organizationId?: string;
        organizationName?: string;
        caseId?: string;
        caseExternalRef?: string;
        subjectId?: string;
        subjectName?: string;
        poiId?: string;
        poiName?: string;
        summaryLabel?: string;
    } | null;
    performer?: Pick<User, 'id' | 'email' | 'firstName' | 'lastName'>;
}

export interface CaseSubjectLink extends Timestamps {
    id: string;
    caseId: string;
    subjectId: string;
    role?: string;
    metadata?: Record<string, unknown>;
    subject?: Subject;
    caseRecord?: CaseRecord;
}

export interface PoiSubjectLink extends Timestamps {
    id: string;
    poiId: string;
    subjectId: string;
    relationshipType: string;
    isCurrent?: boolean;
    validFrom?: string;
    validTo?: string | null;
    metadata?: Record<string, unknown>;
    poi?: PointOfInterest;
}

export interface CasePoiLink extends Timestamps {
    id: string;
    caseId?: string;
    poiId: string;
    role?: string;
    poi?: PointOfInterest;
}

export interface CaseRecord extends Timestamps {
    id: string;
    organizationId: string;
    /** When set, another org performs the field work for this case; null = self-serve. */
    executionOrganizationId?: string | null;
    caseType?: string;
    status?: string;
    externalReferenceId?: string;
    priority?: string;
    caseSchemaId?: string;
    caseSchemaVersion?: number;
    requestedAt?: string;
    dueAt?: string;
    lastObservedAt?: string | null;
    metadata?: Record<string, unknown>;
    organization?: Organization;
    caseSubjects?: CaseSubjectLink[];
    casePois?: CasePoiLink[];
}

/** Per-row outcome of a bulk case import. */
export type CaseImportOutcome = 'created' | 'duplicate' | 'skipped' | 'failed';

/** A row the importer could not create, surfaced so Ops can fix and re-upload. */
export interface CaseImportRowError {
    row: number;
    externalReferenceId?: string;
    outcome: Exclude<CaseImportOutcome, 'created'>;
    reason: string;
}

/** Classifier for a single previewed import row. */
export type CaseImportRowStatus =
    | 'create'
    | 'update'
    | 'duplicate'
    | 'missing_data'
    | 'parse_error';

/** Keys of required fields absent from a row, surfaced so Ops can spot gaps. */
export type CaseImportMissingField = 'loanId' | 'name' | 'phone' | 'address' | 'gps';

/** One row of `POST /cases/import/preview`, addressed by its stable row index. */
export interface CaseImportRowPreview {
    index: number;
    status: CaseImportRowStatus;
    loanId?: string;
    fullName?: string;
    phone?: string;
    businessName?: string;
    address?: string;
    lat?: number | null;
    lon?: number | null;
    loanSize?: string;
    instructions?: string;
    subjectType?: string;
    missingFields: CaseImportMissingField[];
    reason?: string;
}

/** Result of `POST /cases/import/preview` — nothing is persisted. */
export interface CaseImportPreviewResult {
    fileName: string;
    totalRows: number;
    rows: CaseImportRowPreview[];
}

/** Lightweight projection of a created case, enough to drive monitoring setup. */
export interface CreatedCaseProjection {
    id: string;
    externalReferenceId?: string | null;
    caseType?: string;
    status?: string;
    organizationId?: string | null;
}

/** Result of `POST /cases/import` — counts per outcome plus the rejected rows. */
export interface CaseImportResult {
    fileName?: string;
    created: number;
    duplicate: number;
    skipped: number;
    failed: number;
    errors?: CaseImportRowError[];
    createdCases?: CreatedCaseProjection[];
}

/** Result of applying a monitoring plan across a batch of cases. */
export interface MonitoringPlanResult {
    updated: number;
    visitsGenerated: number;
    skipped?: number;
}

export interface VisitPoiLink extends Timestamps {
    id: string;
    visitId: string;
    poiId: string;
    verificationScope?: string;
    poi?: PointOfInterest;
}

export interface VisitData {
    instructions?: string;
    flagged: boolean;
    flagReason?: string;
    flagDetails?: {
        reportId?: string;
        notes?: string;
        mismatches?: Array<{
            key: string;
            subjectValue?: unknown;
            reportValue?: unknown;
        }>;
    };
    /** QA finding: 'no_material_change' | 'material_change_identified' */
    qaAnalysis?: string;
    /** Reviewer notes recorded when approving a visit. */
    approvalNotes?: string;
    /** Reason code recorded when rejecting a visit. */
    rejectionReason?: string;
    /** Free-text comments recorded when rejecting a visit. */
    rejectionComments?: string;
}

export interface Visit extends Timestamps {
    id: string;
    caseId: string;
    visitType?: string;
    status?: string;
    fieldOfficerId?: string;
    /** Latest active assignment metadata (cleared after unassignment). */
    reason?: string | null;
    assignedBy?: string | null;
    assignedAt?: string | null;
    assignedByName?: string | null;
    startedAt?: string;
    completedAt?: string;
    data?: VisitData;
    caseRecord?: CaseRecord;
    fieldOfficer?: User;
    visitPois?: VisitPoiLink[];
    report?: {
        id: string;
        submittedAt: string;
        data: { gps_coordinates?: string };
        media?: Array<{
            id: string;
            reportId: string;
            mediaType: string;
            category: string;
            downloadUrl?: string | null;
            downloadUrlExpiresInSeconds?: number;
        }>;
    } | null;
}

export interface ReportSignal extends Timestamps {
    id: string;
    reportId: string;
    signalType: string;
    signalValue: Record<string, unknown>;
}

export interface PoiFact extends Timestamps {
    id: string;
    poiId: string;
    factType: string;
    factValue: Record<string, unknown>;
    confidenceScore?: string;
    lastVerifiedAt?: string;
}

export interface PoiVerification extends Timestamps {
    id: string;
    poiId: string;
    caseId?: string;
    visitId?: string;
    verificationType?: string;
    verificationResult?: string;
    verificationMethod?: string;
    confidenceScore?: string;
    notes?: string;
    verifiedAt?: string;
    subjectSnapshot?: Record<string, unknown> | null;
}

export interface ReportMedia {
    id: string;
    reportId: string;
    mediaType: string;
    category: string;
    storageKey?: string | null;
    originalUrl?: string | null;
    mimeType?: string | null;
    downloadUrl?: string | null;
    downloadUrlExpiresInSeconds?: number;
    capturedAt?: string | null;
    metadata?: Record<string, unknown>;
}

export interface Report extends Timestamps {
    id: string;
    visitId: string;
    workflowType: string;
    reportSchemaId?: string;
    reportSchemaVersion?: number;
    data?: Record<string, unknown>;
    summary?: string;
    submittedAt?: string | null;
    visit?: Visit;
    signals?: ReportSignal[];
}

export interface SubjectFact extends Timestamps {
    id: string;
    subjectId: string;
    factType: string;
    factValue: Record<string, unknown>;
    confidenceScore?: string;
    lastVerifiedAt?: string | null;
}

export interface SubjectMedia extends Timestamps {
    id: string;
    subjectId: string;
    mediaType: string;
    category: string;
    originalUrl?: string | null;
    storageKey?: string | null;
    mimeType?: string | null;
    downloadUrl?: string | null;
    downloadUrlExpiresInSeconds?: number;
}

export interface Subject extends Timestamps {
    id: string;
    subjectType?: string;
    fullName?: string;
    primaryPhone?: string;
    alternativePhone?: string;
    claimedBusinessName?: string;
    attributes?: Record<string, unknown>;
    lastVerifiedAt?: string | null;
    facts?: SubjectFact[];
    media?: SubjectMedia[];
    caseSubjects?: CaseSubjectLink[];
    poiSubjects?: PoiSubjectLink[];
}

export interface PointOfInterest extends Timestamps {
    id: string;
    poiType?: string;
    name?: string;
    description?: string;
    claimedAddress?: string;
    claimedCity?: string;
    claimedState?: string;
    claimedLatitude?: string;
    claimedLongitude?: string;
    verificationStatus?: string;
    lastVerifiedAt?: string | null;
    attributes?: Record<string, unknown>;
    facts?: PoiFact[];
    verifications?: PoiVerification[];
}

export type DuplicateReviewEntityType = 'subject' | 'poi';
export type DuplicateReviewMergeEntityType = 'subject' | 'poi';

export interface DuplicateReviewCluster {
    clusterId: string;
    entityType: DuplicateReviewEntityType;
    score: number | string;
    reasons: string[];
    records: Array<Record<string, unknown>>;
    impact: Record<string, number | string>;
}

export interface DuplicateReviewClusterPage {
    data: DuplicateReviewCluster[];
    total: number;
    page: number;
    limit: number;
    hasMore?: boolean;
}

export interface DuplicateReviewOperation {
    table: string;
    action: 'reassign' | 'soft_delete_duplicates' | 'soft_delete_entities';
    count: number;
}

export interface DuplicateReviewMergeResult {
    entityType: DuplicateReviewMergeEntityType;
    canonicalId: string;
    duplicateIds: string[];
    operations: DuplicateReviewOperation[];
    warnings: string[];
}

export interface PaginatedResult<T> {
    data: T[];
    total: number;
}

export interface ReportOverviewCounters {
    totalVolume: number;
    totalValue: number;
    completed: number;
    pendingBacklog: number;
    rejected: number;
    flagged: number;
    averageVisitTatDays: number | null;
}

export interface DepartmentVolumeRow {
    department: string;
    jobs: number;
    totalValue: number;
    completed: number;
    pending: number;
}

export interface ReportOverview {
    counters: ReportOverviewCounters;
    departments: DepartmentVolumeRow[];
}

export type LocationStatus = 'close' | 'within_range' | 'out_of_range' | 'extremely_far';

export interface CompletedJobRow {
    visitId: string;
    caseId: string;
    customerName: string | null;
    department: string;
    dateAdded: string | null;
    dateVerified: string | null;
    visitTatDays: number | null;
    caseTatDays: number | null;
    verifiedDurationDays: number | null;
    distanceAddressMeters: number | null;
    locationStatus: LocationStatus | null;
}

export interface PendingBacklogRow {
    visitId: string;
    caseId: string;
    customerName: string | null;
    status: string;
    dateAdded: string | null;
}

export interface ReportingAgent {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
}

export interface AgentSubmissions {
    agentId: string;
    daily: number;
    weekly: number;
    monthly: number;
    yearly: number;
}

export type SubmissionPeriod = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface AgentSubmissionJobRow {
    reportId: string;
    visitId: string;
    caseId: string;
    customerName: string | null;
    status: string;
    submittedAt: string | null;
}

export interface TokenPair {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
}

/** Organization membership embedded in the access token JWT. */
export interface JwtOrganizationClaim {
    id: string;
    name?: string;
    type?: string;
    roles: string[];
}

/**
 * Decoded access token payload (mirrors `AuthenticationService.generateTokens`).
 * Includes spread user fields plus `sub`, `platformRoles`, `organizations`, `infos`.
 */
export interface JwtPayload {
    sub: string;
    id?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    isPlatformAdmin?: boolean;
    platformRoles?: string[];
    organizations?: JwtOrganizationClaim[];
    infos?: Record<string, unknown>;
    iat?: number;
    exp?: number;
}

export const AUDIT_ENTITY_TYPES = [
    'case',
    'case_schema',
    'case_subject',
    'case_poi',
    'subject',
    'point_of_interest',
    'visit',
    'visit_assignment',
    'visit_poi',
    'poi_subject',
    'poi_verification',
    'subject_poi_preverification_invite',
    'poi_fact',
    'subject_fact',
    'subject_media',
    'data_source',
    'report',
    'report_schema',
    'report_media',
    'report_signal',
    'organization',
    'organization_member',
    'role',
] as const;

export const AUDIT_ACTIONS = ['create', 'update', 'delete', 'merge', 'submit', 'transition_status'] as const;

// ---------------------------------------------------------------------------
// Monitoring state
// ---------------------------------------------------------------------------

export interface MonitoringState {
    businessStatus?: string;
    subjectRelationship?: string;
    place?: string;
    asset?: string;
    inventory?: string;
    [key: string]: string | undefined;
}

export interface StateChange {
    dimension: string;
    label: string;
    previous: string | null;
    current: string | null;
}

export interface MonitoringTimelineEntry {
    visitId: string;
    visitType: string;
    submittedAt: string;
    agentName?: string;
    raw: MonitoringState;
    cumulative: MonitoringState;
    changes: StateChange[];
    isBaseline: boolean;
}

export interface MonitoringStateResponse {
    current: MonitoringState;
    previous: MonitoringState | null;
    changes: StateChange[];
    timeline: MonitoringTimelineEntry[];
    monitoringPlan?: {
        frequency: string;
        intervalEvery?: number | null;
        intervalUnit?: string | null;
    } | null;
}
